import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Since we're already in a web environment, we don't need a WebView component
// We'll simply create an iframe to display the external content
const WebViewScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { url: urlParam } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get URL from location state or URL parameter, with proper decoding
  let urlToLoad = '';

  if (location.state?.url) {
    // Prefer state for security and to handle complex URLs
    urlToLoad = location.state.url;
  } else if (urlParam) {
    try {
      // Decode URL parameter if provided
      urlToLoad = decodeURIComponent(urlParam);
    } catch (e) {
      setError(t('app.webView.invalidUrl'));
      urlToLoad = 'about:blank';
    }
  } else {
    // Fallback 
    urlToLoad = 'about:blank';
    setError(t('app.webView.noUrl'));
  }

  // Validate URL to have proper protocol
  if (urlToLoad && urlToLoad !== 'about:blank' && !urlToLoad.startsWith('http')) {
    urlToLoad = 'https://' + urlToLoad;
  }

  // Handle iframe load events
  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenInBrowser = () => {
    if (urlToLoad && urlToLoad !== 'about:blank') {
      window.open(urlToLoad, '_blank');
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack}>
          <Text style={styles.backButton}>{t('app.webView.back')}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('app.webView.title')}</Text>
        <TouchableOpacity onPress={handleOpenInBrowser}>
          <Text style={styles.openButton}>{t('app.webView.openInBrowser')}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.webViewContainer}>
        {loading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color="#0066cc" />
            <Text style={styles.loadingText}>{t('app.webView.loadingContent')}</Text>
          </View>
        )}

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <iframe
          src={urlToLoad}
          style={styles.iframe}
          title="External Content"
          sandbox="allow-scripts allow-same-origin allow-forms"
          loading="lazy"
          onLoad={handleIframeLoad}
          onError={() => {
            setLoading(false);
            setError(t('app.webView.failedLoad'));
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    zIndex: 10,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  backButton: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    transition: 'all 0.2s',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.01em',
  },
  openButton: {
    color: 'var(--accent-primary)',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
  },
  webViewContainer: {
    flex: 1,
    backgroundColor: 'white',
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-deep-void)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
    fontFamily: 'Outfit, sans-serif',
  },
  errorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 5,
    backdropFilter: 'blur(10px)',
  },
  errorText: {
    color: 'var(--text-primary)',
    fontSize: 16,
    textAlign: 'center',
    padding: 24,
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.3)',
    borderRadius: 16,
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
});

export default WebViewScreen; 